import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useDarkMode } from "../../../context/ThemeContext";
import {
  useYardInspectionsStore,
  updateYardInspection,
  createManualExitForInspection,
  updateYardInspectionRegistration,
  uploadYardInspectionEvidence,
  updateYardInspectionEvidence,
  deleteYardInspectionEvidence,
  deriveInspectionStatus,
  inspectionDateMillis,
  hasLostExit,
  inspectionStatusLabel,
  inspectionStatusClasses,
  inspectionStatusAccentClasses,
  inspectionStatusIcon,
} from "../../../store/oasis/yardInspectionsStore";
import type {
  YardInspection,
  InspectionStatus,
  UpdateYardInspectionParams,
  UpdateYardInspectionRegistrationParams,
} from "../../../store/oasis/yardInspectionsStore";
import {
  registrationErrorKey,
  specialInspectionKind,
} from "../../../store/oasis/specialInspection";
import SpecialTagBadge from "../../../components/inspections/SpecialTagBadge";
import { useTruckYardStore } from "../../../store/truckYardStore";
import type { TruckYardVehicle } from "../../../store/truckYardStore";
import { useAllTruckYardVehicles } from "../../../features/truckYard/queries";
import { useAllGroups } from "../../../features/catalogs/queries";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import {
  fetchTruckYardOptions,
  type TruckYardOption,
} from "../../../utils/truckYardOptions";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import {
  SyncModal,
  runYardSync,
  type SyncFormData,
} from "../../patios-maniobras/components/YardSync";
import {
  fetchYardSyncStatus,
  createManualAccessEvent,
} from "../../../store/oasis/hikvisionAccessStore";
import { brand } from "../../../config/theme";
import Swal from "sweetalert2";
import InspectionReportModal from "./InspectionReportModal";
import { fetchAllYardInspections } from "./fetchAllYardInspections";
import YardInspectionDetailsModal from "../components/YardInspectionDetailsModal";
import { openYardGate } from "../../patios-maniobras/openYardGate";
import { useAlert } from "../../../context/AlertContext";
import useTruckYardRole, {
  filterByYardScope,
  resolveMultiYardScopedRequest,
} from "../../../hooks/useTruckYardRole";
import MultiSelectFilter from "../../patios-maniobras/components/MultiSelectFilter";
import { syncYardsForSelection } from "../../patios-maniobras/components/yardMultiFilter";
import NoAssignedYardsNotice from "../../../components/base/NoAssignedYardsNotice";
import ExportButtons from "../../reports/components/ExportButtons";
import {
  InspectionStatusPill,
  INSPECTION_STATUS_ORDER,
} from "../../../components/inspections/InspectionStatusPill";
import type { ReportTableColumn } from "../../reports/components/ReportPointsTable";
import {
  DEFAULT_YARD_TIME_ZONE,
  yardDateInputDaysAgo,
  yardTodayDateInput,
} from "../../../utils/yardDay";
import { deepLinkStep } from "./deepLinkWiden";
import {
  formatYardTsMinutes,
  inspectionTimePoints,
  toYardMillis,
  type InspectionTimePoint,
} from "../inspectionTimes";
import { escapeHtml } from "../../../utils/escapeHtml";

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 6;

// ── Export rows ────────────────────────────────────────────────────────────────
// Flattened, display-ready shape for the Excel/PDF export (reuses the reports'
// exportHistoric utility).

interface InspectionExportRow {
  id: string;
  vehicle: string;
  driver: string;
  yard: string;
  entry: string;
  exit: string;
  evidences: number;
  status: string;
}

const EXPORT_COLUMNS: ReportTableColumn<InspectionExportRow>[] = [
  { key: "vehicle", header: "Vehículo / Placa" },
  { key: "driver", header: "Conductor" },
  { key: "yard", header: "Patio" },
  { key: "entry", header: "Entrada" },
  { key: "exit", header: "Salida" },
  { key: "evidences", header: "Evidencias" },
  { key: "status", header: "Estado" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// El día en curso EN EL PATIO, no en el navegador. Es la fecha que viaja al
// backend, y el backend la lee como hora de pared del patio (yardDateRange.js):
// calculada con el calendario del navegador, un admin conectado desde otra zona
// pedía el día equivocado y veía la pantalla vacía sin ninguna indicación.
//
// Desde #496 la pantalla SÍ manda `yard_id`, así que el backend ya no resuelve
// con su fallback sino con la zona del patio (`resolveYardTimeZone`,
// yardDateRange.js): el cliente tiene que usar la misma o los dos extremos
// discrepan y nadie lo dice en pantalla. Sin patio elegido —el supervisor
// mirando el agregado de los suyos— los dos lados vuelven a caer en el mismo
// fallback, que es por lo que sigue siendo el valor por defecto.
function todayDateInput(timeZone: string = DEFAULT_YARD_TIME_ZONE): string {
  return yardTodayDateInput(timeZone);
}

function formatYardTs(ts: unknown): string {
  const millis = toYardMillis(ts);
  if (!millis) return "—";
  return new Date(millis).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// Compact human duration (e.g. "2d 3h", "3h 20m", "45m"). Used by the
// time-in-yard pill.
function formatDurationShort(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Time-in-yard pill ──────────────────────────────────────────────────────
// How long the vehicle has been in the yard, in one of three tenses:
//
//   closed        "Estuvo 3h 20m"        entry → exit, a finished fact
//   open, fresh   "En patio 3h 20m"      live, amber, counting against `now`
//   open, lost    "Estuvo al menos 83d"  a floor, not a clock
//
// The third case is the whole point. The pill used to branch on
// `access_exit_id` alone, so a visit the status pill right next to it already
// called "Sin salida" still rendered amber "En patio 83d 4h", ticking upward —
// contradicting the modal, which says in as many words that the unit has most
// likely long since left and the exit lane simply never read it. Once the exit
// is presumed lost, elapsed time stops measuring a truck standing in the yard
// and starts measuring how long the record has been broken, so it stops
// counting, drops the amber, and says "at least".
//
// `now` comes from the section's shared tick rather than from Date.now() at
// render time: this pill sits beside a status derived from the same clock, and
// reading it a second time here is what let the two disagree. It is passed to
// hasLostExit for the same reason — the pill and the status pill must decide
// "is this exit lost?" from the same instant or they contradict each other for
// up to 30 s.
function YardTimePill({
  inspection,
  now,
  className = "",
}: {
  inspection: YardInspection;
  now: number;
  className?: string;
}) {
  const entry = toYardMillis(inspection.entry_timestamp);
  if (!entry) return null;
  const hasExit = !!inspection.access_exit_id;
  const end = hasExit ? toYardMillis(inspection.exit_timestamp) : now;
  if (hasExit && !end) return null;
  const lostExit = !hasExit && hasLostExit(inspection, now);
  const duration = formatDurationShort(end - entry);

  // Neutral gray for both settled cases (closed, and open-but-lost): neither is
  // a live counter, and the alarm for a lost exit is already carried by the
  // orange status pill and the card border. Amber is reserved for the one case
  // where the number really is ticking.
  const live = !hasExit && !lostExit;
  const title = hasExit
    ? "Tiempo total en patio"
    : lostExit
      ? "Tiempo transcurrido desde la entrada. No se registró la salida, así que es un mínimo, no el tiempo que la unidad estuvo en el patio."
      : "Tiempo en patio (en curso)";
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
        live
          ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
          : "bg-gray-500/15 text-gray-400 border border-gray-500/30"
      } ${className}`}
    >
      <i className={live ? "ri-timer-flash-line" : "ri-time-line"}></i>
      {hasExit
        ? `Estuvo ${duration}`
        : lostExit
          ? `Estuvo al menos ${duration}`
          : `En patio ${duration}`}
    </span>
  );
}

// ── Las cuatro horas de la visita ─────────────────────────────────────────────
// Geocerca entrada → pluma entrada → pluma salida → geocerca salida, en ese
// orden y siempre las cuatro: una fila que desaparece cuando falta el dato se
// lee igual que una que nunca existió, y lo que el operador necesita saber es
// justamente que el GPS no registró el cruce. Qué texto lleva cada una lo
// decide `inspectionTimePoints` (puro y testeado); acá sólo se pinta.
//
// La pluma y la geocerca se distinguen por ícono (caja = pluma, pin = geocerca)
// además de por la etiqueta, y la dirección por color, con el mismo verde/rojo
// que ya usaba esta tarjeta para entrada/salida.
const TIME_POINT_ICON: Record<InspectionTimePoint["key"], string> = {
  geofence_entry: "ri-map-pin-add-line",
  gate_entry: "ri-login-box-line",
  gate_exit: "ri-logout-box-line",
  geofence_exit: "ri-map-pin-2-line",
};

function timePointIconClass(point: InspectionTimePoint): string {
  if (point.missing) return "";
  return point.direction === "entry" ? "text-green-400" : "text-red-400";
}

// Sólo la salida de pluma ausente va en amarillo: es la única de las cuatro
// ausencias que le pide algo al operador (ligar la salida), y es el mismo
// amarillo que tenía el "Pendiente" al que reemplaza. Un cruce de geocerca
// ausente es informativo —no toda unidad lleva GPS— y no debe gritar.
function timePointValueClass(
  point: InspectionTimePoint,
  themeClasses: any,
): string {
  if (!point.missing) return themeClasses.text;
  return point.key === "gate_exit"
    ? "text-yellow-400 italic"
    : `${themeClasses.textSecondary} italic`;
}

function InspectionTimesList({
  inspection,
  timeZone,
  themeClasses,
}: {
  inspection: YardInspection;
  timeZone?: string;
  themeClasses: any;
}) {
  const points = inspectionTimePoints(inspection, timeZone);
  return (
    <dl
      className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 text-xs ${themeClasses.textSecondary}`}
    >
      {points.map((point) => (
        // `contents`: cada punto aporta tres celdas a la MISMA grilla, así las
        // horas quedan alineadas en columna entre las cuatro filas sin fijar
        // anchos — la tarjeta ocupa todo el ancho en un teléfono (grid-cols-1)
        // y un tercio en escritorio.
        <div key={point.key} className="contents">
          <i
            aria-hidden="true"
            className={`${TIME_POINT_ICON[point.key]} ${timePointIconClass(point)}`}
          ></i>
          <dt className="truncate">{point.label}</dt>
          {/* `title`: cuando la hora es una cota ("antes de…") y no una lectura,
              el dato crudo del GPS queda a un hover. En la tarjeta no cabe una
              segunda línea por fila; el modal sí la muestra entera. */}
          <dd
            title={point.note}
            className={`text-right tabular-nums whitespace-nowrap ${timePointValueClass(point, themeClasses)}`}
          >
            {point.text}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Par entrada/salida de UN sistema, apilado: para la fila de lista y la tabla,
// que no tienen el encabezado de fecha de la tarjeta y por eso usan `fullText`
// (fecha + hora) en vez de la hora sola.
function InspectionTimePair({
  entry,
  exit,
  themeClasses,
}: {
  entry: InspectionTimePoint;
  exit: InspectionTimePoint;
  themeClasses: any;
}) {
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      {[entry, exit].map((point) => (
        <span
          key={point.key}
          title={point.note ? `${point.label}. ${point.note}` : point.label}
          className={`flex items-center gap-1 whitespace-nowrap tabular-nums ${timePointValueClass(point, themeClasses)}`}
        >
          <i
            aria-hidden="true"
            className={`${TIME_POINT_ICON[point.key]} ${timePointIconClass(point)}`}
          ></i>
          <span className="sr-only">{point.label}: </span>
          {point.fullText}
        </span>
      ))}
    </div>
  );
}

// ── Status Pill ──────────────────────────────────────────────────────────────
//
// El componente se mudó a components/inspections/InspectionStatusPill.tsx en
// #499: la tarjeta de patio pinta la MISMA tira y con el mismo orden, y una
// segunda copia del hue es exactamente lo que ya falló una vez (ver
// INSPECTION_STATUS_PALETTE en yardInspectionsStore.ts).

// ── Insignia de inspección especial ──────────────────────────────────────────
//
// "Especial · Proveedor" / "Especial · Pipa sin tag". Va aparte de la píldora
// de estado porque dice otra cosa: el estado es en qué punto está la visita, la
// insignia de dónde salió. Una pipa sin tag recorre los estados normales
// ("Sin vehículo", "En patio", "Completa") y la insignia es lo único que la
// distingue de una unidad con tarjeta propia en cada uno de ellos.
function SpecialInspectionBadge({
  inspection,
  className,
}: {
  inspection: YardInspection;
  className?: string;
}) {
  const kind = specialInspectionKind(inspection);
  return kind ? <SpecialTagBadge kind={kind} className={className} /> : null;
}

// ── Card ─────────────────────────────────────────────────────────────────────

function YardInspectionCard({
  inspection,
  status,
  vehicle,
  now,
  timeZone,
  themeClasses,
  isDarkMode,
  onDetails,
  onReport,
}: {
  inspection: YardInspection;
  status: InspectionStatus;
  vehicle: TruckYardVehicle | null;
  now: number;
  timeZone?: string;
  themeClasses: any;
  isDarkMode: boolean;
  onDetails: (i: YardInspection) => void;
  onReport: (i: YardInspection) => void;
}) {
  const hasVehicle = !!vehicle || !!inspection.vehicle_plate;
  const vehicleName = vehicle?.name || inspection.vehicle_plate || null;
  const vehicleDetail = vehicle
    ? [vehicle.plate, vehicle.brand, vehicle.model].filter(Boolean).join(" · ")
    : null;
  const evidenceCount = inspection.evidences?.length ?? 0;
  const firstThumb = inspection.evidences?.[0]?.imageUrl;

  return (
    <div
      className={`${themeClasses.cardBg} border ${inspectionStatusAccentClasses(status)} rounded-xl p-4 hover:shadow-lg transition-all flex flex-col gap-3`}
    >
      {/* Status + date header */}
      <div className="flex items-center justify-between">
        <span
          className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1.5 ${inspectionStatusClasses(status)}`}
        >
          <i className={`${inspectionStatusIcon(status)} text-sm`}></i>
          {inspectionStatusLabel(status)}
        </span>
        <span
          className={`text-xs ${themeClasses.textSecondary} flex items-center gap-1`}
        >
          <i className="ri-calendar-line"></i>
          {/* La fecha de la VISITA (entrada, con salida de respaldo), no la
              del ingest: `createdAt` es cuándo la plataforma escribió el
              documento, y el sync del controlador escribe hoy visitas de hace
              semanas. La tarjeta decía "hoy" de una visita de junio.

              En la zona del PATIO, igual que las cuatro horas de abajo. Esas
              horas omiten la fecha cuando caen en el mismo día que esta
              cabecera, así que las dos tienen que hablar del mismo día: con la
              cabecera en la zona del navegador, una entrada a las 23:30 del
              patio vista desde una hora más al este decía "día siguiente
              00:30" al lado de un "23:30:12" suelto. */}
          {formatYardTsMinutes(inspectionDateMillis(inspection), timeZone)}
        </span>
      </div>

      {specialInspectionKind(inspection) && (
        <div className="flex">
          <SpecialInspectionBadge inspection={inspection} />
        </div>
      )}

      {/* Title */}
      {inspection.title && (
        <p className={`text-sm font-semibold ${themeClasses.text} truncate`}>
          {inspection.title}
        </p>
      )}

      {/* Vehicle */}
      <div
        className={`flex items-center gap-2 p-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
      >
        <i
          className={`ri-truck-line ${hasVehicle ? "text-primary-500" : themeClasses.textSecondary} flex-shrink-0`}
        ></i>
        <div className="min-w-0">
          <p
            className={`text-xs font-medium ${hasVehicle ? themeClasses.text : `italic ${themeClasses.textSecondary}`} truncate`}
          >
            {vehicleName || "Sin vehículo asignado"}
          </p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {vehicleDetail || "Vehículo"}
          </p>
        </div>
      </div>

      {/* Driver */}
      <div
        className={`flex items-center gap-2 p-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
      >
        <i
          className={`ri-user-line ${inspection.driver_name ? "text-primary-500" : themeClasses.textSecondary} flex-shrink-0`}
        ></i>
        <div className="min-w-0">
          <p
            className={`text-xs font-medium ${inspection.driver_name ? themeClasses.text : `italic ${themeClasses.textSecondary}`} truncate`}
          >
            {inspection.driver_name || "Sin conductor"}
          </p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>Conductor</p>
        </div>
      </div>

      {/* Las cuatro horas: geocerca y pluma, entrada y salida */}
      <InspectionTimesList
        inspection={inspection}
        timeZone={timeZone}
        themeClasses={themeClasses}
      />

      {/* Time in yard */}
      {inspection.entry_timestamp && (
        <div className="flex">
          <YardTimePill inspection={inspection} now={now} />
        </div>
      )}

      {/* Yard + evidence count */}
      <div className="flex items-center justify-between gap-2">
        <div
          className={`flex items-center gap-1.5 text-xs ${themeClasses.textSecondary} min-w-0`}
        >
          <i className="ri-building-4-line"></i>
          <span className="truncate">{inspection.yard_name || "—"}</span>
        </div>
        {evidenceCount > 0 && (
          <div className={`flex items-center gap-1.5 flex-shrink-0`}>
            {firstThumb && (
              <img
                src={firstThumb}
                alt=""
                className="w-6 h-6 rounded object-cover"
              />
            )}
            <span
              className={`text-xs font-medium ${themeClasses.textSecondary} flex items-center gap-1`}
            >
              <i className="ri-camera-line"></i>
              {evidenceCount}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-auto pt-1">
        <button
          onClick={() => onDetails(inspection)}
          className="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          <i className="ri-eye-line"></i>
          Ver / Completar
        </button>
        <button
          onClick={() => onReport(inspection)}
          className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
          title="Generar reporte PDF"
        >
          <i className="ri-file-chart-line"></i>
        </button>
      </div>
    </div>
  );
}

// ── List Row ─────────────────────────────────────────────────────────────────

function YardInspectionListRow({
  inspection,
  status,
  vehicle,
  now,
  timeZone,
  themeClasses,
  isDarkMode,
  onDetails,
  onReport,
}: {
  inspection: YardInspection;
  status: InspectionStatus;
  vehicle: TruckYardVehicle | null;
  now: number;
  timeZone?: string;
  themeClasses: any;
  isDarkMode: boolean;
  onDetails: (i: YardInspection) => void;
  onReport: (i: YardInspection) => void;
}) {
  const hasVehicle = !!vehicle || !!inspection.vehicle_plate;
  const displayName =
    vehicle?.name || inspection.vehicle_plate || "Sin vehículo";
  const displaySub = vehicle
    ? [vehicle.plate, vehicle.brand, vehicle.model]
        .filter(Boolean)
        .join(" · ") ||
      inspection.driver_name ||
      "Sin conductor"
    : inspection.driver_name || "Sin conductor";
  const evidenceCount = inspection.evidences?.length ?? 0;
  const [geofenceEntry, gateEntry, gateExit, geofenceExit] =
    inspectionTimePoints(inspection, timeZone);

  return (
    <div
      className={`${themeClasses.cardBg} border ${inspectionStatusAccentClasses(status)} rounded-xl p-4 hover:shadow-lg transition-all`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${hasVehicle ? "bg-primary-500/20" : isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
          >
            <i
              className={`ri-truck-line ${hasVehicle ? "text-primary-500" : themeClasses.textSecondary} text-base`}
            ></i>
          </div>

          <div className="min-w-0 w-44 flex-shrink-0">
            <h3
              className={`font-semibold ${themeClasses.text} truncate text-sm`}
            >
              {displayName}
            </h3>
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {displaySub}
            </p>
          </div>

          <div className="hidden md:block min-w-0 w-28 flex-shrink-0">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Patio
            </p>
            <p className={`text-xs ${themeClasses.text} truncate`}>
              {inspection.yard_name || "—"}
            </p>
          </div>

          <div className="hidden lg:block min-w-0 w-40 flex-shrink-0">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Pluma
            </p>
            <InspectionTimePair
              entry={gateEntry}
              exit={gateExit}
              themeClasses={themeClasses}
            />
          </div>

          {/* xl y no lg: son 160 px más en una fila que a 1024 ya va justa con
              el menú lateral abierto. Por debajo de xl los cruces siguen a un
              toque, en la vista de tarjetas y en el modal. */}
          <div className="hidden xl:block min-w-0 w-40 flex-shrink-0">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              Geocerca
            </p>
            <InspectionTimePair
              entry={geofenceEntry}
              exit={geofenceExit}
              themeClasses={themeClasses}
            />
          </div>

          {evidenceCount > 0 && (
            <span
              className={`hidden sm:flex items-center gap-1 text-xs ${themeClasses.textSecondary} flex-shrink-0`}
            >
              <i className="ri-camera-line"></i>
              {evidenceCount}
            </span>
          )}

          <span
            className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 flex-shrink-0 ${inspectionStatusClasses(status)}`}
          >
            <i className={`${inspectionStatusIcon(status)} text-xs`}></i>
            {inspectionStatusLabel(status)}
          </span>

          <SpecialInspectionBadge
            inspection={inspection}
            className="flex-shrink-0"
          />

          {inspection.entry_timestamp && (
            <YardTimePill
              inspection={inspection}
              now={now}
              className="hidden md:inline-flex flex-shrink-0"
            />
          )}
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => onDetails(inspection)}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
            style={{ backgroundColor: brand.primary }}
            title="Ver detalles"
          >
            <i className="ri-eye-line text-white text-sm"></i>
          </button>
          <button
            onClick={() => onReport(inspection)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
            title="Generar reporte PDF"
          >
            <i className="ri-file-chart-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Table (dense tabular view) ────────────────────────────────────────────────

interface InspectionTableRow {
  data: YardInspection;
  status: InspectionStatus;
  vehicle: TruckYardVehicle | null;
}

function InspectionTable({
  rows,
  now,
  timeZoneOf,
  themeClasses,
  isDarkMode,
  onDetails,
  onReport,
}: {
  rows: InspectionTableRow[];
  now: number;
  timeZoneOf: (yardId: string) => string | undefined;
  themeClasses: any;
  isDarkMode: boolean;
  onDetails: (i: YardInspection) => void;
  onReport: (i: YardInspection) => void;
}) {
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const headCls = `sticky top-0 z-10 px-3 py-2.5 text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium border-b ${themeClasses.cardBorder} ${surface} text-left whitespace-nowrap`;
  const cellCls = `px-3 py-2.5 ${themeClasses.text} whitespace-nowrap`;

  return (
    <div className={`overflow-x-auto rounded-xl border ${themeClasses.cardBorder}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className={headCls}>Estado</th>
            <th className={headCls}>Vehículo / Tag</th>
            <th className={headCls}>Conductor</th>
            <th className={headCls}>Patio</th>
            <th className={headCls}>Pluma (entrada / salida)</th>
            <th className={headCls}>Geocerca (entrada / salida)</th>
            <th className={headCls}>Evidencias</th>
            <th className={`${headCls} text-right`}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ data: i, status, vehicle }) => {
            const displayName =
              vehicle?.name || i.vehicle_plate || i.card_number || "—";
            const displaySub = vehicle
              ? [vehicle.plate, vehicle.brand].filter(Boolean).join(" · ")
              : "";
            const evidenceCount = i.evidences?.length ?? 0;
            const [geofenceEntry, gateEntry, gateExit, geofenceExit] =
              inspectionTimePoints(i, timeZoneOf(i.yard_id));
            return (
              <tr
                key={`insp-tr-${i.id}`}
                className={`border-t ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.buttonHover} transition-colors`}
              >
                <td className={cellCls}>
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${inspectionStatusClasses(status)}`}>
                      <i className={`${inspectionStatusIcon(status)} text-xs`}></i>
                      {inspectionStatusLabel(status)}
                    </span>
                    <SpecialInspectionBadge inspection={i} />
                  </div>
                </td>
                <td className={cellCls}>
                  <div className="flex flex-col">
                    <span className={`font-medium ${themeClasses.text} truncate max-w-[220px]`}>{displayName}</span>
                    {displaySub && (
                      <span className={`text-xs ${themeClasses.textSecondary} truncate max-w-[220px]`}>{displaySub}</span>
                    )}
                  </div>
                </td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>{i.driver_name || "—"}</td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>{i.yard_name || "—"}</td>
                <td className={cellCls}>
                  <div className="flex flex-col gap-1 items-start">
                    <InspectionTimePair
                      entry={gateEntry}
                      exit={gateExit}
                      themeClasses={themeClasses}
                    />
                    {i.entry_timestamp && (
                      <YardTimePill inspection={i} now={now} />
                    )}
                  </div>
                </td>
                <td className={cellCls}>
                  <InspectionTimePair
                    entry={geofenceEntry}
                    exit={geofenceExit}
                    themeClasses={themeClasses}
                  />
                </td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>
                  {evidenceCount > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-camera-line"></i>{evidenceCount}
                    </span>
                  ) : "—"}
                </td>
                <td className={`${cellCls} text-right`}>
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => onDetails(i)}
                      className="w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors"
                      style={{ backgroundColor: brand.primary }}
                      title="Ver detalles"
                    >
                      <i className="ri-eye-line text-white text-sm"></i>
                    </button>
                    <button
                      onClick={() => onReport(i)}
                      className={`w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                      title="Generar reporte PDF"
                    >
                      <i className="ri-file-chart-line text-sm"></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Section ─────────────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

export default function InspectionHistorySection() {
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();

  // Check if user is a supervisor/inspector with yard restrictions
  const { getYardScope, canSelectAllYards } = useTruckYardRole();
  const yardScope = getYardScope();
  // Sin patios asignados no hay ningún rango que pedir: sin `yard_id`, la
  // consulta traería las inspecciones de los siete patios de la empresa para
  // que `filteredYardInspections` las descarte una por una, cada 5 s. Ver
  // NoAssignedYardsNotice para lo que se ve en su lugar.
  const noAssignedYards = yardScope.mode === "none";

  const {
    inspections: yardInspections,
    setInspections: setYardInspections,
    updateInspectionInList,
  } = useYardInspectionsStore();

  const { t } = useTranslation("truckYard");
  const selectStyles = getCustomSelectStyles(isDarkMode);

  const vehiclesFromStore = useTruckYardStore((s) => s.vehicles);
  useAllTruckYardVehicles();
  const groups = useGroupsStore((s) => s.groups);
  useAllGroups();

  const vehiclesById = useMemo(() => {
    const map = new Map<string, TruckYardVehicle>();
    vehiclesFromStore.forEach((v) => map.set(v.id, v));
    return map;
  }, [vehiclesFromStore]);

  // Filter inspections by assigned yards for supervisors/inspectors.
  //
  // El `|| assignedYardIds.length === 0` que había acá hacía fallar ABIERTO: en
  // un rol restringido la lista vacía significa "sin patios asignados", no "sin
  // restricción", y devolvía TODAS las inspecciones. Es el mismo cambio de una
  // línea que #497 hizo en las pantallas hermanas, y acá urgía más porque #497
  // ensancha el agujero por partida doble: `hasYardRestrictions()` ya no incluye
  // a los admins, y "rol restringido con cero patios" pasó de accidente a estado
  // de primera clase, con su propio cartel.
  //
  // ── El memo depende del ALCANCE, no de dos valores que se recrean ─────────
  //
  // Las dependencias eran `[yardInspections, assignedYardIds, hasYardRestrictions]`
  // y las dos últimas eran nuevas en CADA render: `getUserAssignedYardIds()`
  // devuelve un array recién parseado de localStorage y `hasYardRestrictions` es
  // una función redefinida por el hook. O sea que el memo no memoizaba nunca — y
  // no se notaba, porque el resultado era correcto: lo que se perdía era el
  // trabajo, no la respuesta. Es el mismo defecto que #528 corrigió en
  // `getYardScope()`, y se corrige igual: el valor se estabiliza ARRIBA, con un
  // `useMemo` dentro del hook, porque memoizar en el call site no sirve cuando
  // lo que entra ya viene recreado.
  //
  // Cuánto costaba, medido antes de tocar nada (node 24.16, la cascada real de
  // esta pantalla: allItems → baseFiltered → statusCounts → filtered →
  // exportRows, que es lo que este memo dispara al devolver un array nuevo):
  //
  //   500 inspecciones   el filtro solo 0.006 ms   la cascada entera  1.1 ms
  //   5 000              0.061 ms                             11.4 ms
  //   10 000             0.135 ms                             22.5 ms
  //
  // 500 es el rango de un día (`YARD_INSPECTIONS_PAGE_SIZE`); 10 000 es el techo
  // absoluto (20 páginas) y sólo se alcanza con el rango ampliado del deep link
  // o limpiando las fechas, que además baja el poll a 30 s. En el tamaño en que
  // esta pantalla realmente corre, un render desperdiciado cuesta ~1 ms: NO es
  // una mejora de rendimiento y no se vende como tal. Se arregla porque un
  // `useMemo` que no memoiza es una afirmación falsa en el código —el próximo
  // que lea estas dependencias va a creer que el pipeline está protegido— y
  // porque el alcance estable ya está a mano.
  //
  // TODO(#496): falta la pantalla de #496 propiamente dicha — el cartel de "sin
  // patios asignados" y el recorte del lado del servidor. Esto es sólo el cierre
  // del fallo abierto.
  const filteredYardInspections = useMemo(
    () => filterByYardScope(yardScope, yardInspections, (i) => i.yard_id),
    [yardInspections, yardScope],
  );

  // ── UI state ────────────────────────────────────────────────────────────
  const [selectedView, setSelectedView] = useState<"grid" | "list" | "table">(
    "grid",
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<InspectionStatus | null>(
    null,
  );
  // ── El default es el DÍA EN CURSO, no "todo" ──────────────────────────────
  //
  // Arrancaban vacíos, lo que mandaba la petición sin `date_from`/`date_to` y
  // el backend lo lee como "sin rango": `fetchAllYardInspections` barría hasta
  // 20 páginas de 500 —el histórico completo de la empresa— y lo repetía cada
  // 5 s, con una subconsulta de `evidences` por documento del lado del
  // servidor. Con el sync creando una inspección por entrada, abrir la
  // pantalla era un barrido completo de la colección cada cinco segundos.
  //
  // El operador que abre el historial quiere el turno de hoy. Los inputs
  // Desde/Hasta y el botón de limpiar siguen ahí para ampliar el rango a mano.
  const [dateFrom, setDateFrom] = useState(todayDateInput);
  const [dateTo, setDateTo] = useState(todayDateInput);

  // ── El día en curso se recalcula; no se congela al montar ─────────────────
  //
  // `todayDateInput` corría sólo en el inicializador de `useState`, así que una
  // pestaña abierta cruzando la medianoche quedaba pidiendo el día de ayer para
  // siempre. Antes daba lo mismo porque el rango no viajaba al backend; ahora
  // sí viaja, y estas pantallas se dejan abiertas todo el turno — un monitor
  // 24/7 simplemente dejaba de ver el día nuevo.
  //
  // Se exige que AMBOS extremos sigan valiendo el default vigente antes de
  // tocar nada. Compararlos por separado le movía "Hasta" a las 00:00 al
  // operador que sólo había cambiado "Desde": o los dos siguen siendo el
  // default, o no se toca ninguno. Un minuto de resolución alcanza para un
  // límite diario.
  const appliedDefaultDayRef = useRef(todayDateInput());
  // Access-screen-style filters.
  // Lo que eligió el usuario, en crudo —una LISTA: el combo es de selección
  // múltiple, igual que en Accesos y en Vehículos—. Los patios EFECTIVOS salen
  // de `resolveMultiYardScopedRequest` unas líneas más abajo (issue #496).
  const [yardPicks, setYardPicks] = useState<string[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [yardOptionsList, setYardOptionsList] = useState<TruckYardOption[]>([]);

  // ── El combo de patio y el patio que VIAJA (issue #496) ───────────────────
  //
  // Esta pantalla llenaba el combo con `fetchTruckYardOptions()` —el catálogo
  // entero de la empresa— para cualquier rol, con el placeholder "Todos los
  // patios" y `isClearable`. Para un inspector eso significaba dos cosas malas
  // a la vez: elegir un patio ajeno devolvía una lista vacía, que en esta
  // pantalla se lee como "ese patio no tuvo inspecciones" y no como "no es
  // tuyo"; y el estado por defecto (`""`) pedía al backend el rango de TODOS
  // los patios para después descartarlos en el navegador
  // (`filteredYardInspections`).
  //
  // Se resuelve en render y no en un efecto correctivo, porque el `useQuery` de
  // abajo dispara con la query-key del PRIMER commit: con un efecto, la primera
  // petición de cada montaje saldría igual sin `yard_id`.
  //
  // `resolveMultiYardScopedRequest` (que delega en `resolveYardFilter`) en vez
  // de `resolveYardFilter` a secas para que los params de patio
  // (`yardFilter.params`) y el `enabled` salgan de la misma decisión que el
  // combo, y no de dos expresiones escritas a mano que tienen que coincidir. Es
  // lo que hace testeable "la primera petición de este montaje lleva `yard_id`"
  // sin montar la pantalla — ver multiYardScope.test.ts.
  //
  // Es la misma función que usa Accesos: con UN patio elegido la petición es
  // `{ yard_id }`, byte a byte la de antes; con varios viaja `yard_ids=a,b`, que
  // `/yardInspectionList` ya entiende desde #708.
  const canAllYards = canSelectAllYards();
  const yardFilter = useMemo(
    () =>
      resolveMultiYardScopedRequest({
        scope: yardScope,
        canSelectAllYards: canAllYards,
        yards: yardOptionsList,
        selectedYardIds: yardPicks,
      }),
    [yardScope, canAllYards, yardOptionsList, yardPicks],
  );
  // La lista efectiva es un arreglo NUEVO cada vez que la memo de arriba se
  // recalcula —lo hace al llegar el catálogo aunque la selección no cambie—, así
  // que lo que entra a las dependencias y a la query-key es la clave string, que
  // compara por valor. Los ids son auto-ids de Firestore: no llevan comas.
  const selectedYardKey = yardFilter.selectedYardIds.join(",");
  const selectedYardIds = useMemo(
    () => (selectedYardKey ? selectedYardKey.split(",") : []),
    [selectedYardKey],
  );
  // La MISMA lista para el combo de filtro y para el modal de Sync. Mientras
  // fueron dos, el Sync de esta pantalla ofrecía los siete patios de la empresa
  // a un supervisor de dos, y sincronizar el log de un controlador ajeno ESCRIBE
  // (crea inspecciones): mostrar de más es un bug, escribir de más es otra cosa.
  const selectableYards = yardFilter.options;
  // Lo que el modal de Sync ofrece y sincroniza. Con varios patios elegidos se
  // recorta a ESOS: el "todos" del modal no puede escribir en patios que el
  // usuario dejó fuera del filtro. Ver syncYardsForSelection.
  const syncTargets = useMemo(
    () => syncYardsForSelection(selectableYards, selectedYardIds),
    [selectableYards, selectedYardIds],
  );

  // La zona con la que se arma "hoy", ahora que el patio viaja (ver
  // todayDateInput). Sin patio elegido es el mismo fallback que aplica el
  // backend cuando no recibe `yard_id`, así que los dos extremos coinciden. Con
  // varios, la del PRIMERO de la selección, que es la que usa el backend
  // (`resolveYardTimeZone(requestedYardIds[0])` en /yardInspectionList): los
  // dos lados tienen que cortar el mismo día, o vuelve #472.
  const firstSelectedYard = selectedYardIds[0] || "";
  const yardTimeZone =
    (firstSelectedYard
      ? selectableYards.find((y) => y.id === firstSelectedYard)?.timeZone
      : null) || DEFAULT_YARD_TIME_ZONE;

  // ── El día en curso se recalcula; no se congela al montar ─────────────────
  //
  // `todayDateInput` corría sólo en el inicializador de `useState`, así que una
  // pestaña abierta cruzando la medianoche quedaba pidiendo el día de ayer para
  // siempre. Antes daba lo mismo porque el rango no viajaba al backend; ahora
  // sí viaja, y estas pantallas se dejan abiertas todo el turno — un monitor
  // 24/7 simplemente dejaba de ver el día nuevo.
  //
  // El segundo motivo lo agrega #496: al montar todavía no llegó el catálogo,
  // así que la zona es el fallback; cuando se resuelve el patio —o el operador
  // cambia a otro con otra zona— "hoy" puede ser otro día. Por eso `apply()`
  // corre también al entrar al efecto y no sólo cada minuto.
  //
  // Se exige que AMBOS extremos sigan valiendo el default vigente antes de
  // tocar nada. Compararlos por separado le movía "Hasta" a las 00:00 al
  // operador que sólo había cambiado "Desde": o los dos siguen siendo el
  // default, o no se toca ninguno. Un minuto de resolución alcanza para un
  // límite diario.
  useEffect(() => {
    const apply = () => {
      const today = todayDateInput(yardTimeZone);
      const applied = appliedDefaultDayRef.current;
      if (today === applied) return;
      if (dateFrom !== applied || dateTo !== applied) return;
      appliedDefaultDayRef.current = today;
      setDateFrom(today);
      setDateTo(today);
    };
    apply(); // cambio de zona del patio
    const id = window.setInterval(apply, 60000); // medianoche
    return () => window.clearInterval(id);
  }, [yardTimeZone, dateFrom, dateTo]);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedInspection, setSelectedInspection] =
    useState<YardInspection | null>(null);
  const [reportInspection, setReportInspection] =
    useState<YardInspection | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Poll cada 5s (refetchInterval) para que las inspecciones autocreadas tras un
  // swipe aparezcan sin recarga manual. El rango de fechas va en la query-key.
  // Write-through al store viejo porque otros flujos leen `inspections` y mutan
  // con `updateInspectionInList`.
  //
  // Se pide el rango COMPLETO, no una primera página. Antes eran `limit: 200`
  // sin `page`: por encima de 200 inspecciones en rango la pantalla describía
  // sólo las 200 más recientes y las píldoras de estado, que cuentan sobre ese
  // mismo conjunto, informaban "Completas 140" queriendo decir "140 entre las
  // 200 más recientes". Ver fetchAllYardInspections.ts para por qué el conteo
  // no puede venir agregado del backend y por qué traerlo entero no cuesta
  // lecturas de más.
  //
  // ── `include_open` va SÓLO en el default, no en cualquier rango ───────────
  //
  // Suma al conjunto las visitas que siguen abiertas, entren cuando entren. Es
  // lo que impide que el día en curso borre al camión que entró el jueves y no
  // salió: medido en producción el 3/9/2026, el día estricto daba
  // `missing_exit 0` y `missing_vehicle 0` mientras había 27 visitas abiertas
  // de días anteriores, y con esto pasan a 17 y 10.
  //
  // Pero esa justificación es sobre el DEFAULT. El operador que acota a
  // propósito a un día pasado —1/8 al 1/8, para revisar ese turno— no está
  // pidiendo una pantalla de vigilancia: pidió un día. Mandarlo siempre le
  // metía en ese día las visitas abiertas de los últimos 30, incluidas las que
  // entraron DESPUÉS del `date_to` que eligió, y como las píldoras y el export
  // se calculan sobre el mismo conjunto, los números de ese día quedaban mal y
  // el PDF los imprimía bajo "Período: 1/8 al 1/8". El requisito es que si
  // cambian los filtros cambien los números, no que haya filas que ningún
  // filtro explica.
  //
  // El flag va en la query-key: cambiar de rango tiene que refetchear, no
  // reusar la respuesta del otro modo.
  const isDefaultDayRange =
    dateFrom === appliedDefaultDayRef.current &&
    dateTo === appliedDefaultDayRef.current;
  // ── El poll de 5 s es del DÍA EN CURSO, no de cualquier rango ─────────────
  //
  // Los 5 s existen por una razón concreta: que la inspección que el backend
  // autocrea tras un pase aparezca sin recargar. Eso sólo pasa en el día en
  // curso. Un rango histórico —el que deja la ampliación de un deep link, o
  // limpiar las fechas— no recibe filas nuevas en tiempo real, y repetirle cada
  // 5 s un barrido de hasta 20 páginas de 500 documentos con una subconsulta de
  // `evidences` por documento es puro costo sin nada que mostrar.
  //
  // Esto es lo que hace coherentes las dos ramas del deep link: `open` puede
  // dejar el rango ampliado —hay algo que mirar— sin dejar armado el poll
  // rápido, y `giveUp` lo restaura porque no hay nada que mirar. Y acota
  // también el "limpiar fechas", que es la misma situación por otra puerta.
  const REFETCH_LIVE_MS = 5000;
  const REFETCH_WIDE_MS = 30000;
  const inspectionsQuery = useQuery({
    // Los patios van en la clave, no sólo en los params: sin ellos, cambiar la
    // selección reusaría la respuesta cacheada de la anterior.
    queryKey: [
      "yardInspections",
      {
        dateFrom,
        dateTo,
        includeOpen: isDefaultDayRange,
        yardIds: selectedYardKey,
      },
    ],
    queryFn: () =>
      fetchAllYardInspections({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        // El patio VIAJA (issue #496). Además de recortar lo que el servidor
        // devuelve —que es el punto—, es lo que le permite al backend resolver
        // el día contra la zona del patio: `resolveYardTimeZone` cae en su
        // fallback `America/Mexico_City` justamente cuando no recibe `yard_id`
        // (yardDateRange.js), así que hasta ahora los dos extremos del rango
        // los calculaba la misma zona por defecto para todos los patios.
        ...yardFilter.params,
        ...(isDefaultDayRange ? { include_open: true } : {}),
      }),
    refetchInterval: isDefaultDayRange ? REFETCH_LIVE_MS : REFETCH_WIDE_MS,
    // Con el alcance en `none` no hay `yard_id` posible y la consulta sin él
    // traería la empresa entera: no se pide nada y la pantalla lo explica.
    // Sale de la misma decisión que los params, no de una condición paralela,
    // para que las dos no se desincronicen al editarse por separado. Ojo con lo
    // que eso NO dice: `params` vacío con la petición habilitada es normal y
    // correcto para el admin y para el "todos" del supervisor. El caso que este
    // `enabled` cubre es el otro, el de `none`.
    enabled: yardFilter.enabled,
  });
  const yardLoading = inspectionsQuery.isLoading && !noAssignedYards;

  useEffect(() => {
    if (inspectionsQuery.data) setYardInspections(inspectionsQuery.data.data);
  }, [inspectionsQuery.data, setYardInspections]);

  // Sólo si se tocó el techo de fetchAllYardInspections. Es el único caso en el
  // que lo que se ve sigue siendo un recorte, y entonces hay que decirlo: los
  // conteos de abajo describen `loadedTotal`, no `rangeTotal`.
  const rangeTruncated = inspectionsQuery.data?.truncated ?? false;
  const rangeTotal = inspectionsQuery.data?.total ?? 0;
  const loadedTotal = inspectionsQuery.data?.data.length ?? 0;

  // ── Deep link `?inspection=<id>` ──────────────────────────────────────────
  //
  // Lo usa la pantalla de accesos para saltar del evento a su inspección
  // (accessSection/page.tsx, "ver inspección vinculada"). Con el default acotado
  // al día en curso, la inspección pedida puede no estar en el conjunto
  // cargado: su entrada fue el mes pasado y ya cerró, así que ni el rango ni
  // `include_open` la traen. El efecto se quedaba esperando un `match` que no
  // iba a llegar y —peor— tampoco limpiaba el query param, de modo que el
  // enlace quedaba muerto y pegado en la URL.
  //
  // Un deep link tiene que traer su objetivo aunque esté fuera del rango por
  // defecto. La decisión de qué paso dar vive en deepLinkWiden.ts, que explica
  // la escalera y está cubierta por tests; acá sólo se aplica.
  //
  // El rango ampliado queda puesto y a la vista en los inputs: llegaste acá
  // siguiendo un enlace a algo viejo, y devolverte al día en curso escondería
  // lo que veniste a ver.
  //
  // OJO con qué conjunto se le pasa: tiene que ser `inspectionsQuery.data.data`
  // —el mismo objeto cuya llegada reporta `dataUpdatedAt`— y NO el array del
  // store. El store lo escribe el efecto de arriba, que corre en el mismo
  // commit: cuando llegan los datos del rango ampliado, `dataUpdatedAt` ya es
  // nuevo pero el array del store todavía es el del rango anterior, así que la
  // escalera se saltaba peldaños y la inspección de entre 30 y 365 días no
  // abría nunca. Ver el test de regresión en deepLinkWiden.test.ts.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef<string | null>(null);
  const widenedForRef = useRef<string | null>(null);
  const widenStepRef = useRef(0);
  const widenedKeyRef = useRef<string | null>(null);
  const loadedInspections = inspectionsQuery.data?.data;
  // Identifica el rango al que pertenece `loadedInspections`. Es la parte de la
  // query-key que la escalera necesita: react-query sólo entrega `data` de la
  // key vigente, así que las fechas de ESTE render describen el conjunto de
  // este render. `includeOpen` no hace falta acá porque se deriva de las mismas
  // dos fechas.
  const loadedRangeKey = `${dateFrom}|${dateTo}`;
  useEffect(() => {
    const requestedId = searchParams.get("inspection");
    if (!requestedId) return;
    if (autoOpenedRef.current === requestedId) return;

    const action = deepLinkStep<YardInspection>({
      requestedId,
      loaded: loadedInspections,
      dataUpdatedAt: inspectionsQuery.dataUpdatedAt,
      loadedKey: loadedRangeKey,
      widenedFor: widenedForRef.current,
      widenStep: widenStepRef.current,
      widenedKey: widenedKeyRef.current,
    });

    if (action.type === "wait") return;

    if (action.type === "widen") {
      // La clave se guarda ANTES de pedir el cambio de estado y se arma con las
      // mismas dos fechas que van a la query-key, para que el peldaño se
      // destrabe en cuanto llegue —o se sirva del caché— el conjunto de ESTE
      // rango. Ver el gate de deepLinkWiden.ts.
      // Los dos extremos con la zona del PATIO, no con el fallback: el rango
      // ampliado viaja al backend igual que el del día en curso, y desde #496
      // el backend lo interpreta con `resolveYardTimeZone(yard_id)`. Con el
      // fallback acá, "hace 7 días" del cliente y del servidor podían ser dos
      // días distintos en un patio fuera de UTC−6 — que es el bug de #472
      // exactamente, sólo que aplicado al peldaño de ampliación.
      const nextFrom = yardDateInputDaysAgo(action.days, yardTimeZone);
      const nextTo = todayDateInput(yardTimeZone);
      widenedForRef.current = requestedId;
      widenStepRef.current = action.step;
      widenedKeyRef.current = `${nextFrom}|${nextTo}`;
      setDateFrom(nextFrom);
      setDateTo(nextTo);
      return;
    }

    autoOpenedRef.current = requestedId;

    if (action.type === "open") {
      // El rango ampliado se deja puesto: hay algo que mirar, y devolver al día
      // en curso escondería justo lo que se vino a ver. El costo de dejarlo no
      // es el de antes: con el rango ampliado el refetch baja a 30 s (ver la
      // query de arriba) y la pantalla lo dice en el encabezado.
      setSelectedInspection(action.inspection);
    } else {
      // `giveUp`: acá no hay nada que mirar, así que no hay nada que justifique
      // conservar un rango de un año. Se vuelve al día en curso.
      const today = todayDateInput(yardTimeZone);
      appliedDefaultDayRef.current = today;
      setDateFrom(today);
      setDateTo(today);
    }

    const next = new URLSearchParams(searchParams);
    next.delete("inspection");
    setSearchParams(next, { replace: true });
  }, [
    loadedInspections,
    loadedRangeKey,
    searchParams,
    setSearchParams,
    inspectionsQuery.dataUpdatedAt,
    // Sin esto el efecto se queda con la zona del render en que se creó, que al
    // montar es siempre el fallback porque el catálogo todavía no llegó: las
    // fechas que arma acá saldrían con una zona y el backend las leería con
    // otra. Volver a correrlo es inocuo — `autoOpenedRef` y el `!requestedId`
    // de arriba lo hacen idempotente.
    yardTimeZone,
  ]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // ── Decorate inspections with status + vehicle lookup ───────────────────
  type DecoratedItem = {
    data: YardInspection;
    status: InspectionStatus;
    vehicle: TruckYardVehicle | null;
  };

  // ── Shared clock ────────────────────────────────────────────────────────
  // `missing_exit` is the one status derived from the clock rather than from
  // the document, so "when did we read the clock" became a correctness
  // question. Two things went wrong without a single tick:
  //
  //  · The status memo only recomputed when its inputs changed identity, and
  //    react-query's structural sharing hands back the SAME array whenever a
  //    poll returns unchanged data — the common case on a quiet yard. A visit
  //    sitting at 7 h 55 m would therefore stay "En patio" indefinitely and
  //    only flip to "Sin salida" when some unrelated document happened to
  //    change. (For a yard-restricted supervisor it used to recompute on every
  //    render instead, because `filteredYardInspections` depended on the fresh
  //    array getUserAssignedYardIds() returns each time — so the same screen
  //    aged differently depending on the role. That memo now hangs off the
  //    stable yard scope, so BOTH roles are on this tick and nothing else; the
  //    tick is what keeps the status honest, not the accident above.)
  //  · YardTimePill read Date.now() at render time, far more often than the
  //    memo ran, so the duration and the status it is drawn next to were read
  //    from two different clocks.
  //
  // One tick fixes both: status, counts and durations all come from the same
  // instant. 30 s because the pill prints minutes — coarser and the minute
  // would visibly lag; finer buys nothing against an 8 h threshold.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const allItems = useMemo<DecoratedItem[]>(() => {
    const items: DecoratedItem[] = filteredYardInspections.map((i) => ({
      data: i,
      status: deriveInspectionStatus(i, now),
      vehicle: i.vehicle_id ? (vehiclesById.get(i.vehicle_id) ?? null) : null,
    }));
    // Por fecha de ENTRADA (salida de respaldo, ingest como último recurso),
    // igual que el filtro que trajo estas filas y que el orden del backend.
    // Ordenar por `createdAt` ponía arriba lo que el sync acababa de importar:
    // una visita de junio encabezaba la lista de hoy porque su documento se
    // escribió hace un minuto. Ver inspectionDateMillis.
    items.sort(
      (a, b) => inspectionDateMillis(b.data) - inspectionDateMillis(a.data),
    );
    return items;
  }, [filteredYardInspections, vehiclesById, now]);

  // Vehicles belonging to the selected transporter group (join on vehicle_ids).
  const groupVehicleIds = useMemo(() => {
    if (!selectedGroup) return null;
    const group = groups.find((g) => g.id === selectedGroup);
    return new Set(group?.vehicle_ids || []);
  }, [selectedGroup, groups]);

  // ── Filtering ───────────────────────────────────────────────────────────
  // Two stages, same shape as the access screen's alert pills: `baseFiltered`
  // is everything EXCEPT the status pill, so the counts printed on the pills
  // describe the set the user is actually looking at and stay put while the
  // pills are toggled. Counting over the unfiltered list instead made the
  // pills lie the moment any other filter was on — pick a yard, read "En
  // patio 12", click it, get 2, because the 12 counted every yard in the
  // company.
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((item) => {
      const y = item.data;
      // También recorta acá, y no sólo en la petición: con el filtro en "todos"
      // el conjunto cargado es el del alcance entero, y un backend sin
      // `yard_ids` lo ignoraría y devolvería de más.
      if (selectedYardIds.length > 0 && !selectedYardIds.includes(y.yard_id)) {
        return false;
      }
      if (selectedVehicle && y.vehicle_id !== selectedVehicle) return false;
      if (groupVehicleIds && (!y.vehicle_id || !groupVehicleIds.has(y.vehicle_id))) {
        return false;
      }
      if (q) {
        // The vehicle's own name/plate are in here because that is what the
        // card actually prints: the headline is `vehicle?.name ||
        // vehicle_plate`, and the inspection's copy of the plate is null on
        // every visit the Acuapue tags produce (card → vehicle, no plate on
        // the event). Matching only the inspection's fields meant typing the
        // plate you can read on the card returned nothing.
        return (
          (y.vehicle_plate || "").toLowerCase().includes(q) ||
          (item.vehicle?.plate || "").toLowerCase().includes(q) ||
          (item.vehicle?.name || "").toLowerCase().includes(q) ||
          (y.card_number || "").toLowerCase().includes(q) ||
          (y.driver_name || "").toLowerCase().includes(q) ||
          (y.yard_name || "").toLowerCase().includes(q) ||
          (y.title || "").toLowerCase().includes(q) ||
          // La visita de proveedor no tiene unidad: su nombre es lo único por
          // lo que alguien la buscaría.
          (y.provider_name || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allItems, search, selectedYardIds, selectedVehicle, groupVehicleIds]);

  // ── Status counts ───────────────────────────────────────────────────────
  // Derived from `baseFiltered`, and therefore from the same `now` as the list
  // below: a visit can only be counted under the status it is rendered with.
  const statusCounts = useMemo(() => {
    const counts: Record<InspectionStatus, number> = {
      complete: 0,
      cancelled: 0,
      pending_exit: 0,
      missing_exit: 0,
      missing_vehicle: 0,
      incomplete: 0,
      provider_pending: 0,
      provider_complete: 0,
    };
    baseFiltered.forEach((item) => counts[item.status]++);
    return counts;
  }, [baseFiltered]);

  const filtered = useMemo(
    () =>
      statusFilter
        ? baseFiltered.filter((item) => item.status === statusFilter)
        : baseFiltered,
    [baseFiltered, statusFilter],
  );

  // Export rows: the whole filtered set (not just the visible page), formatted
  // like the dense table view.
  const exportRows = useMemo<InspectionExportRow[]>(
    () =>
      filtered.map(({ data: i, status, vehicle }) => {
        const vehicleName = vehicle
          ? [vehicle.name, vehicle.plate].filter(Boolean).join(" · ")
          : i.vehicle_plate || i.card_number || "—";
        return {
          id: i.id,
          vehicle: vehicleName,
          driver: i.driver_name || "—",
          yard: i.yard_name || "—",
          entry: formatYardTs(i.entry_timestamp),
          exit: i.access_exit_id ? formatYardTs(i.exit_timestamp) : "Pendiente",
          evidences: i.evidences?.length ?? 0,
          status: inspectionStatusLabel(status),
        };
      }),
    [filtered],
  );

  // Options for the access-style filter dropdowns.
  // Salen del alcance, no del catálogo: `yardOptionsList` es la respuesta cruda
  // de `/truck-yard/yards` y listarla entera es el defecto de #496.
  const yardOptions = useMemo<SelectOption[]>(
    () => selectableYards.map((y) => ({ value: y.id, label: y.name })),
    [selectableYards],
  );
  const vehicleOptions = useMemo<SelectOption[]>(
    () =>
      vehiclesFromStore.map((v) => ({
        value: v.id,
        label: v.plate ? `${v.name || v.id} · ${v.plate}` : v.name || v.id,
      })),
    [vehiclesFromStore],
  );
  const groupOptions = useMemo<SelectOption[]>(
    () => groups.map((g) => ({ value: g.id, label: g.name })),
    [groups],
  );

  // Authoritative "last DB sync" time — stamped in Firestore on every manual/auto
  // sync run and read back from the backend (same value the access screen shows).
  const syncStatusQuery = useQuery({
    queryKey: ["yardSyncStatus"],
    queryFn: fetchYardSyncStatus,
    refetchInterval: 30000,
  });
  const lastDbSyncAt = useMemo(() => {
    const v = syncStatusQuery.data?.last_sync_at;
    return v ? new Date(v) : null;
  }, [syncStatusQuery.data]);

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp rather than slice blind. The result set can now shrink under the
  // reader with no filter touched: the 5 s poll can drop a row, and — new with
  // `missing_exit` — the clock alone can move a visit out of the status being
  // filtered. On page 3 of "En patio" that left `paginated` empty with the
  // pager still offering three pages and no way back but Anterior.
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    statusFilter,
    dateFrom,
    dateTo,
    selectedYardKey,
    selectedVehicle,
    selectedGroup,
  ]);

  // Load yards for the filter + sync dropdowns.
  useEffect(() => {
    fetchTruckYardOptions()
      .then(setYardOptionsList)
      .catch(() => setYardOptionsList([]));
  }, []);

  // Controller-log sync (same engine as the access screen): pull each yard's
  // history and persist what the live pipeline missed — each entry (re)creating
  // its inspection.
  const handleSync = useCallback(
    async (data: SyncFormData) => {
      setSyncing(true);
      try {
        if (syncTargets.yards.length === 0) {
          Swal.fire({ icon: "info", title: t("access.sync.title"), text: t("access.sync.noYards") });
          return;
        }
        const { totals, failures } = await runYardSync(syncTargets.yards, data, t);
        setSyncOpen(false);

        // Revelar lo recién sincronizado: los registros traen su timestamp
        // histórico del controlador y la lista arranca en el día en curso, así
        // que sin mover el rango el operador lee "creadas N" y no ve ninguna.
        //
        // Y si el rango cambia, el refetch NO se dispara desde acá — el mismo
        // guard que ya tiene la pantalla de accesos (accessSection/page.tsx,
        // `if (!rangeChanges) await loadEvents()`). `setDateFrom`/`setDateTo`
        // no se ven en este closure: `inspectionsQuery.refetch()` corre contra
        // la query-key ANTERIOR, o sea que barre otra vez el día que el usuario
        // está abandonando, mientras el rango nuevo lo trae aparte el cambio de
        // key en el render siguiente. Y ese barrido no es barato: cada uno es
        // una lectura completa de `yardInspections` en rango más una
        // subconsulta de `evidences` por documento (ver
        // fetchAllYardInspections.ts).
        const nextFrom = data.allHistory ? "" : data.from;
        const nextTo = data.to || "";
        const rangeChanges =
          totals.created > 0 && (nextFrom !== dateFrom || nextTo !== dateTo);

        if (totals.created > 0) {
          setDateFrom(nextFrom);
          setDateTo(nextTo);
          setCurrentPage(1);
        }

        if (!rangeChanges) await inspectionsQuery.refetch();
        syncStatusQuery.refetch();
        const summaryHtml = t("access.sync.summary", {
          created: totals.created,
          skipped: totals.skipped,
          inspections: totals.inspections,
        });
        const failuresHtml = failures.length
          ? `<br/><span style="color:#f59e0b">${failures.map(escapeHtml).join("<br/>")}</span>`
          : "";
        Swal.fire({
          icon: totals.total === 0 && failures.length > 0 ? "warning" : "success",
          title: t("access.sync.resultTitle"),
          html: `${summaryHtml}${failuresHtml}`,
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Error";
        Swal.fire({ icon: "error", title: t("common.error"), text: message });
      } finally {
        setSyncing(false);
      }
    },
    [syncTargets, t, inspectionsQuery, dateFrom, dateTo],
  );

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (safePage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (safePage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(safePage - 1);
        pages.push(safePage);
        pages.push(safePage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // ── Handlers ────────────────────────────────────────────────────────────

  // Reintentos de pluma en vuelo ("SDK busy"): se cortan al salir de la
  // pantalla, para que la pluma no se mueva cuando ya nadie la mira.
  const gateAborts = useRef(new Set<AbortController>());
  useEffect(() => {
    const aborts = gateAborts.current;
    return () => aborts.forEach((a) => a.abort());
  }, []);

  // "DAR SALIDA MANUAL" del modal: el backend crea la salida de ESTA visita y
  // la cierra; después se levanta la pluma de salida del patio, por el mismo
  // camino que el alta manual de Accesos (openYardGate). Si la pluma falla, la
  // salida ya quedó registrada: openYardGate lo avisa y no lo deshace.
  const handleManualExit = async (
    inspection: YardInspection,
    survey: { tag_presence?: string | null; tag_position?: string | null },
  ): Promise<boolean> => {
    let updated: YardInspection;
    try {
      updated = await createManualExitForInspection(inspection.id, survey);
    } catch (err: unknown) {
      Swal.fire({
        icon: "error",
        title: "No se registró la salida",
        text: apiErrorText(err, {
          // Según el tipo de visita: a un proveedor no se le piden agua ni fotos.
          InspectionIncomplete:
            inspection.special_tag === "provider"
              ? "Faltan datos del proveedor (proveedor, quién lo recibió y motivo). Complétalos, guárdalos y vuelve a intentar."
              : inspection.special_tag === "untagged"
                ? "La inspección no está completa: registro de la unidad (paso 1), tipo de agua, volumen y las tres fotos. Complétala, guárdala y vuelve a intentar."
                : "La inspección no está completa: unidad, tipo de agua, volumen y las tres fotos. Complétala, guárdala y vuelve a intentar.",
          InvalidTagPresence: "Indica si la unidad trae tag.",
          InvalidTagPosition: "Indica dónde va el tag en el parabrisas.",
          InspectionAlreadyHasExit:
            "Esta visita ya tiene salida (puede que la lectora la haya registrado hace un momento). Recarga la inspección.",
        }),
      });
      return false;
    }
    updateInspectionInList(updated);
    setSelectedInspection(updated);
    if (!inspection.yard_id) {
      // Sin patio no hay pluma que levantar: decirlo, no prometerla.
      Swal.fire({
        icon: "warning",
        title: "Salida registrada",
        text: "La visita quedó cerrada, pero no tiene patio asignado: levanta la pluma a mano.",
      });
      return true;
    }
    Swal.fire({
      icon: "success",
      title: "Salida registrada",
      text: "La visita quedó cerrada. Levantando la pluma de salida…",
      timer: 2500,
      showConfirmButton: false,
    });
    // Sin await: la visita ya está cerrada, así que el modal se libera ahora;
    // la pluma puede tardar hasta 2 min si el controlador está ocupado y avisa
    // por su cuenta. Cada apertura guarda su controlador en el conjunto: dos
    // salidas seguidas abren las dos plumas, y al desmontar se cortan TODAS.
    const abort = new AbortController();
    gateAborts.current.add(abort);
    void openYardGate({ yardId: inspection.yard_id, direction: "exit", t, signal: abort.signal })
      .finally(() => gateAborts.current.delete(abort));
    return true;
  };

  // El código de un ClientError viaja en `response.data.meta.message` (mismo
  // lugar que lee registrationErrorKey); el `message` de axios es genérico.
  const apiErrorText = (err: unknown, known: Record<string, string>): string => {
    const code = (err as { response?: { data?: { meta?: { message?: unknown } } } } | null)
      ?.response?.data?.meta?.message;
    if (typeof code === "string" && known[code]) return known[code];
    return err instanceof Error ? err.message : "Error desconocido";
  };

  // "Nuevo Evento Manual": una entrada como si hubieran pasado el tag de pipas
  // sin tag del patio. El backend pone el número del tag desde la configuración
  // del patio y abre la misma inspección especial que el pase por la lectora;
  // acá se abre su modal en el paso 1 (qué unidad entró) y se levanta la pluma
  // de ENTRADA por el mismo camino que el alta manual de Accesos.
  const [creatingManualEvent, setCreatingManualEvent] = useState(false);
  const handleNewManualEvent = async () => {
    const yards = syncTargets.yards;
    if (yards.length === 0) return;
    // Un patio: se confirma. Varios: se elige en el mismo diálogo.
    const pick = await Swal.fire({
      icon: "question",
      title: "Nuevo evento manual",
      text:
        "Se registrará la entrada de una pipa sin tag, se abrirá su inspección para capturar la unidad y se levantará la pluma de entrada.",
      ...(yards.length > 1
        ? {
            input: "select" as const,
            inputOptions: Object.fromEntries(yards.map((y) => [y.id, y.name])),
            inputValue: firstSelectedYard || yards[0].id,
          }
        : {}),
      showCancelButton: true,
      confirmButtonText: "Registrar entrada",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
    });
    if (!pick.isConfirmed) return;
    const yardId: string = yards.length > 1 ? pick.value : yards[0].id;
    if (!yardId) return;

    setCreatingManualEvent(true);
    let inspectionId: string | null | undefined;
    try {
      const ev = await createManualAccessEvent({
        yard_id: yardId,
        event_type: "entry",
        special_tag_kind: "untagged",
      });
      inspectionId = ev.inspection_id;
    } catch (err: unknown) {
      Swal.fire({
        icon: "error",
        title: "No se registró la entrada",
        text: apiErrorText(err, {
          YardHasNoUntaggedTag:
            "Este patio no tiene configurado el tag de pipas sin tag (Patios › tags especiales).",
        }),
      });
      setCreatingManualEvent(false);
      return;
    }

    // Sin inspección no hay dónde capturar la unidad (createInspectionForAccessEvent
    // falló y el backend sólo lo registró): no se deja entrar a una pipa que
    // después nadie puede identificar.
    if (!inspectionId) {
      Swal.fire({
        icon: "error",
        title: "No se creó la inspección",
        text: "La entrada quedó registrada, pero no se pudo abrir su inspección, así que no se levantó la pluma. NO la registres de nuevo (quedaría duplicada): avisa a soporte para crear la inspección de esa entrada.",
      });
      setCreatingManualEvent(false);
      return;
    }

    // La pluma va en paralelo: la entrada ya quedó y el vigilante captura la
    // unidad mientras sube (o mientras se espera turno del controlador).
    const abort = new AbortController();
    gateAborts.current.add(abort);
    void openYardGate({ yardId, direction: "entry", t, signal: abort.signal })
      .finally(() => gateAborts.current.delete(abort));

    try {
      // La visita nueva todavía no está en la lista: se refresca y se abre.
      const fresh = await inspectionsQuery.refetch();
      const created = fresh.data?.data.find((i) => i.id === inspectionId);
      if (created) setSelectedInspection(created);
      else
        Swal.fire({
          icon: "info",
          title: "Entrada registrada",
          // La lista se filtra por fechas: viendo otro rango, la visita de hoy
          // no aparece. Se dice dónde buscarla en vez de prometer que llega.
          text: "La inspección se creó con fecha de hoy. Si estás viendo otro rango de fechas, regresa a hoy para abrirla y capturar la unidad.",
        });
    } finally {
      setCreatingManualEvent(false);
    }
  };

  const handleSaveYardInspection = async (data: UpdateYardInspectionParams) => {
    try {
      const updated = await updateYardInspection(data);
      updateInspectionInList(updated);
      setSelectedInspection(updated);
      Swal.fire({
        icon: "success",
        title: "Inspección actualizada",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error desconocido";
      Swal.fire({ icon: "error", title: "Error", text: message });
    }
  };

  // Paso 1 de una pipa sin tag. Devuelve si se guardó porque el modal avanza
  // al paso 2 sólo en ese caso; `handleSaveYardInspection` de arriba se traga
  // el error y resuelve igual, que para un formulario de un solo paso alcanza.
  const handleSaveInspectionRegistration = async (
    data: UpdateYardInspectionRegistrationParams,
  ): Promise<boolean> => {
    try {
      const updated = await updateYardInspectionRegistration(data);
      updateInspectionInList(updated);
      setSelectedInspection(updated);
      Swal.fire({
        icon: "success",
        title: t("specialInspection.untagged.savedTitle"),
        timer: 2000,
        showConfirmButton: false,
      });
      return true;
    } catch (err: unknown) {
      Swal.fire({
        icon: "error",
        title: t("common.error"),
        text: t(registrationErrorKey(err)),
      });
      return false;
    }
  };

  const refreshInspectionFromList = (id: string) => {
    const fresh = useYardInspectionsStore
      .getState()
      .inspections.find((i) => i.id === id);
    if (fresh) setSelectedInspection(fresh);
  };

  const handleUploadEvidence = async (
    file: File,
    opts?: { title?: string; description?: string },
  ) => {
    if (!selectedInspection) return;
    try {
      const newEvidence = await uploadYardInspectionEvidence({
        inspectionId: selectedInspection.id,
        file,
        title: opts?.title,
        description: opts?.description,
      });
      const updated: YardInspection = {
        ...selectedInspection,
        evidences: [...(selectedInspection.evidences ?? []), newEvidence],
      };
      updateInspectionInList(updated);
      setSelectedInspection(updated);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "No se pudo subir la fotografía";
      showAlert({ title: message, text: "", icon: "error" });
    }
  };

  const handleUpdateEvidence = async (
    evidenceId: string,
    patch: { title?: string; description?: string },
  ) => {
    if (!selectedInspection) return;
    try {
      const updatedEvidence = await updateYardInspectionEvidence({
        inspectionId: selectedInspection.id,
        evidenceId,
        title: patch.title,
        description: patch.description,
      });
      const updated: YardInspection = {
        ...selectedInspection,
        evidences: (selectedInspection.evidences ?? []).map((e) =>
          e.id === evidenceId ? { ...e, ...updatedEvidence } : e,
        ),
      };
      updateInspectionInList(updated);
      setSelectedInspection(updated);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo actualizar la fotografía";
      showAlert({ title: message, text: "", icon: "error" });
    }
  };

  const handleDeleteEvidence = async (evidenceId: string) => {
    if (!selectedInspection) return;
    try {
      await deleteYardInspectionEvidence(selectedInspection.id, evidenceId);
      const updated: YardInspection = {
        ...selectedInspection,
        evidences: (selectedInspection.evidences ?? []).filter(
          (e) => e.id !== evidenceId,
        ),
      };
      updateInspectionInList(updated);
      setSelectedInspection(updated);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo eliminar la fotografía";
      showAlert({ title: message, text: "", icon: "error" });
    }
  };

  // La zona de CADA patio, para las horas con segundos de tarjetas y modal. No
  // es `yardTimeZone` de arriba: aquélla es la del primer patio de la selección
  // y sirve para cortar el día igual que el backend; con varios patios en
  // pantalla (producción tenía 6 en America/Mexico_City y 1 en
  // America/Monterrey, ver utils/yardDay.ts) cada visita se lee en la hora de
  // pared del suyo. `undefined` —patio fuera del catálogo o sin zona— deja la
  // del navegador, que es lo que ya hace `formatYardTs` en el resto de esta
  // pantalla.
  const yardTimeZoneOf = useMemo(() => {
    const byId = new Map(selectableYards.map((y) => [y.id, y.timeZone]));
    return (yardId: string) => byId.get(yardId) || undefined;
  }, [selectableYards]);

  // ── Render item ─────────────────────────────────────────────────────────
  const renderGridItem = (item: DecoratedItem) => (
    <YardInspectionCard
      key={`yard-${item.data.id}`}
      inspection={item.data}
      status={item.status}
      vehicle={item.vehicle}
      now={now}
      timeZone={yardTimeZoneOf(item.data.yard_id)}
      themeClasses={themeClasses}
      isDarkMode={isDarkMode}
      onDetails={setSelectedInspection}
      onReport={setReportInspection}
    />
  );

  const renderListItem = (item: DecoratedItem) => (
    <YardInspectionListRow
      key={`yard-${item.data.id}`}
      inspection={item.data}
      status={item.status}
      vehicle={item.vehicle}
      now={now}
      timeZone={yardTimeZoneOf(item.data.yard_id)}
      themeClasses={themeClasses}
      isDarkMode={isDarkMode}
      onDetails={setSelectedInspection}
      onReport={setReportInspection}
    />
  );

  // Keep the selected inspection in sync with the polling list so newly added
  // evidence (or a freshly-paired exit event) surfaces while the modal is open.
  useEffect(() => {
    if (selectedInspection) refreshInspectionFromList(selectedInspection.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yardInspections]);

  return (
    // Un solo contenedor con scroll: el header de filtros se va con las
    // tarjetas en vez de quedarse clavado arriba.
    <div className="h-full overflow-y-auto">
      {noAssignedYards && (
        <div className="p-4 sm:px-6">
          <NoAssignedYardsNotice
            isDarkMode={isDarkMode}
            what="ninguna inspección"
          />
        </div>
      )}
      {/* Header */}
      <div
        className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
            >
              Historial de Inspecciones
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              Registro histórico de todas las inspecciones realizadas
            </p>
          </div>

          {/* Last DB sync + Sync button (no create button — inspections are
              created automatically from access entries). */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            {lastDbSyncAt && (
              <span
                title={t("access.live.lastSyncTitle")}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
              >
                {/* Mirrors the access screen's pill: history icon, not a
                    database cylinder — the operator is asking "when", not
                    "against what". */}
                <i className="ri-history-line"></i>
                {t("access.live.lastSync", {
                  time: lastDbSyncAt.toLocaleString("es-MX", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }),
                })}
              </span>
            )}
            {/* Sin patios asignados el Sync queda deshabilitado, igual que en
                Accesos (`actionsDisabled`). No es sólo cosmético: `lockedYardId`
                sale de `yardFilter.locked ? firstSelectedYard : undefined` y con
                `none` el patio efectivo es "", o sea falsy, así que el
                `<select>` del modal se DESBLOQUEABA y volvía a ofrecer "Todos
                los patios" — el mismo desbloqueo por falsy que el comentario de
                `lockedYardId` en accessSection describe. `handleSync` ya
                frenaba la escritura con `syncTargets.yards.length === 0`, pero
                ofrecerlo es exactamente lo que #497 sacó de la pantalla. */}
            <button
              onClick={() => setSyncOpen(true)}
              disabled={noAssignedYards}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors border ${themeClasses.cardBorder} ${
                isDarkMode
                  ? "bg-dark-card text-white hover:bg-dark-surface"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200"
              } ${noAssignedYards ? "opacity-50 cursor-not-allowed" : ""}`}
              title={
                noAssignedYards
                  ? t("access.noAssignedYardsAction")
                  : t("access.syncButton")
              }
            >
              <i className="ri-refresh-line"></i>
              <span className="hidden sm:inline">{t("access.syncButton")}</span>
            </button>
            {/* Igual que Sync: sin patios asignados no hay dónde registrarla. */}
            <button
              onClick={handleNewManualEvent}
              disabled={noAssignedYards || creatingManualEvent}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm transition-colors bg-green-600 hover:bg-green-700 text-white ${
                noAssignedYards || creatingManualEvent ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title="Entrada de una pipa sin tag: abre su inspección y levanta la pluma de entrada"
            >
              {creatingManualEvent ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-add-line"></i>
              )}
              <span className="hidden sm:inline">Nuevo Evento Manual</span>
            </button>
          </div>
        </div>

        {/* Aviso de recorte. Sólo aparece si el rango excede el techo de
            fetchAllYardInspections (10 000 inspecciones): en ese caso las
            píldoras de abajo cuentan sobre lo cargado, no sobre el rango, y
            eso hay que decirlo antes de que alguien lea un total que no lo es.
            Va arriba de las píldoras a propósito — se lee antes que los
            números que califica. */}
        {rangeTruncated && (
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
              isDarkMode
                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                : "border-amber-300 bg-amber-50 text-amber-800"
            }`}
          >
            <i className="ri-alert-line mt-0.5"></i>
            <span>
              Mostrando las {loadedTotal.toLocaleString("es-MX")} inspecciones
              más recientes de {rangeTotal.toLocaleString("es-MX")} en el rango.
              Los conteos de estado describen sólo lo cargado — acotá las fechas
              para verlo completo.
            </span>
          </div>
        )}

        {/* Aviso de rango ampliado. Salir del día en curso —limpiando las
            fechas, eligiendo un rango a mano, o llegando por un deep link que
            tuvo que ampliar— cambia dos cosas que si no son invisibles: la
            actualización deja de ser cada 5 s, y las visitas abiertas de días
            anteriores dejan de sumarse (`include_open` es del default). Un
            clic en "limpiar fechas" dejaba a la pantalla pidiendo el histórico
            completo de la empresa sin ningún indicio; que lo diga es la
            diferencia entre una vía de escape y una trampa. */}
        {!isDefaultDayRange && (
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
              isDarkMode
                ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                : "border-sky-300 bg-sky-50 text-sky-800"
            }`}
          >
            <i className="ri-history-line mt-0.5"></i>
            <span>
              Rango ampliado{!dateFrom && !dateTo ? " (histórico completo)" : ""}:
              se actualiza cada {REFETCH_WIDE_MS / 1000} s y no incluye las
              visitas sin salida de otros días.{" "}
              <button
                type="button"
                className="underline underline-offset-2 font-medium"
                onClick={() => {
                  const today = todayDateInput(yardTimeZone);
                  appliedDefaultDayRef.current = today;
                  setDateFrom(today);
                  setDateTo(today);
                  setCurrentPage(1);
                }}
              >
                Volver al día en curso
              </button>
            </span>
          </div>
        )}

        {/* Status pills. Gated on `baseFiltered`, not on the raw store list:
            the counts describe that set, and gating on the store meant a
            yard-restricted supervisor whose yards had nothing got an empty
            pill strip built from another yard's inspections. Because
            `baseFiltered` ignores the status pill itself, clicking a pill down
            to zero results never hides the strip or its "Limpiar filtro". */}
        {baseFiltered.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {INSPECTION_STATUS_ORDER.map(
              (s) =>
                statusCounts[s] > 0 && (
                  <InspectionStatusPill
                    key={s}
                    status={s}
                    count={statusCounts[s]}
                    active={statusFilter === s}
                    onClick={() =>
                      setStatusFilter(statusFilter === s ? null : s)
                    }
                  />
                ),
            )}
            {statusFilter && (
              <button
                type="button"
                onClick={() => setStatusFilter(null)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-all ${themeClasses.buttonText} ${themeClasses.buttonHover} border ${themeClasses.cardBorder}`}
              >
                <i className="ri-close-line mr-1"></i>Limpiar filtro
              </button>
            )}
          </div>
        )}

        {/* Filters (yard / vehicle / group) — mirrors the access screen. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {/* Selección múltiple, el mismo combo que Accesos y Vehículos: quien
              supervisa varios patios los revisa juntos en vez de recorrerlos de
              a uno. Sin ninguno elegido es "todos los del alcance". */}
          <MultiSelectFilter
            isDarkMode={isDarkMode}
            options={yardOptions}
            values={selectedYardIds}
            onChange={setYardPicks}
            // Un inspector nunca está viendo "todos los patios", así que el
            // placeholder tampoco se lo dice, y sin la opción "todos" el combo
            // no se puede limpiar: vaciarlo pediría la empresa entera (#496).
            // Si quita el último chip, `resolveMultiYardScopedRequest` lo
            // devuelve a su patio en el mismo render.
            placeholder={
              yardFilter.allowAll
                ? t("access.filters.allYards")
                : t("access.filters.yard")
            }
            isClearable={yardFilter.allowAll}
            isDisabled={yardFilter.locked}
            noOptionsMessage={t("access.filters.noYards")}
          />
          <Select
            options={vehicleOptions}
            value={vehicleOptions.find((o) => o.value === selectedVehicle) || null}
            onChange={(o) => setSelectedVehicle((o as SelectOption | null)?.value || "")}
            styles={selectStyles}
            placeholder={t("access.filters.allVehicles")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
          <Select
            options={groupOptions}
            value={groupOptions.find((o) => o.value === selectedGroup) || null}
            onChange={(o) => setSelectedGroup((o as SelectOption | null)?.value || "")}
            styles={selectStyles}
            placeholder={t("access.filters.allGroups")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:max-w-xs">
              <i
                className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}
              ></i>
              <input
                type="text"
                placeholder="Buscar placa, tag, conductor, patio o título..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border ${themeClasses.border} ${
                  isDarkMode
                    ? "bg-dark-card text-white placeholder-gray-500"
                    : "bg-white text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>

            {/* Rango por fecha de ENTRADA (salida de respaldo). Arranca en el
                día en curso; limpiarlo vuelve a pedir el histórico completo,
                que es la vía de escape deliberada, no el default. */}
            <div className="flex items-center gap-2">
              <i
                className={`ri-calendar-line ${themeClasses.textSecondary} text-sm hidden sm:inline`}
                title="Rango de fechas"
              ></i>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`px-2 py-2 text-sm rounded-lg border ${themeClasses.border} ${
                  isDarkMode
                    ? "bg-dark-card text-white"
                    : "bg-white text-gray-900"
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                aria-label="Desde"
              />
              <span className={`text-xs ${themeClasses.textSecondary}`}>—</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className={`px-2 py-2 text-sm rounded-lg border ${themeClasses.border} ${
                  isDarkMode
                    ? "bg-dark-card text-white"
                    : "bg-white text-gray-900"
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                aria-label="Hasta"
              />
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                  className={`text-xs px-2 py-1.5 rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonHover} border ${themeClasses.cardBorder}`}
                  title="Limpiar fechas"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <ExportButtons
              rows={exportRows}
              columns={EXPORT_COLUMNS}
              meta={{
                title: "Historial de Inspecciones",
                // El sufijo aparece SÓLO cuando el conjunto de verdad lleva
                // visitas de fuera del período: un PDF que dijera "Período: 3/9
                // al 3/9" a secas estaría mintiendo sobre filas con entrada de
                // agosto. En un rango elegido a mano no se agrega nada, así que
                // el encabezado describe exactamente lo que hay abajo.
                period:
                  dateFrom || dateTo
                    ? `Período: ${dateFrom || "inicio"} al ${dateTo || "hoy"}` +
                      (isDefaultDayRange ? " (+ visitas sin salida registrada)" : "")
                    : `${filtered.length} inspección(es)`,
                fileBase: "historial_inspecciones",
              }}
            />
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "grid"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("grid")}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "list"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("list")}
              >
                <i className="ri-list-check"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "table"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("table")}
                title={t("common.tableView")}
              >
                <i className="ri-table-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {yardLoading && yardInspections.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[50vh] py-16">
            <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              Cargando inspecciones...
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[50vh] py-16">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
            >
              <i
                className={`ri-clipboard-line text-3xl ${themeClasses.textSecondary}`}
              ></i>
            </div>
            <h3 className={`text-lg font-semibold ${themeClasses.text} mb-1`}>
              {search || statusFilter || dateFrom || dateTo
                ? "Sin resultados"
                : "No hay inspecciones aún"}
            </h3>
            <p
              className={`text-sm ${themeClasses.textSecondary} text-center max-w-xs`}
            >
              {search || statusFilter || dateFrom || dateTo
                ? "Intenta con otro término de búsqueda, rango de fechas o cambia el filtro"
                : "Las inspecciones se crean automáticamente cuando se registra un evento de entrada en el patio"}
            </p>
          </div>
        ) : selectedView === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map(renderGridItem)}
          </div>
        ) : selectedView === "table" ? (
          <InspectionTable
            rows={paginated}
            now={now}
            timeZoneOf={yardTimeZoneOf}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={setSelectedInspection}
            onReport={setReportInspection}
          />
        ) : (
          <div className="space-y-3">{paginated.map(renderListItem)}</div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex justify-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
            >
              Anterior
            </button>

            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === "number" && setCurrentPage(page)}
                disabled={page === "..."}
                className={`px-3 py-1 rounded ${
                  page === safePage
                    ? "bg-primary-500 text-white"
                    : page === "..."
                      ? "bg-gray-200 text-gray-400 cursor-default"
                      : "bg-gray-200 text-gray-700"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, safePage + 1))
              }
              disabled={safePage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Details modal */}
      {selectedInspection && (
        <YardInspectionDetailsModal
          inspection={selectedInspection}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          vehicles={vehiclesFromStore}
          yardTimeZone={yardTimeZoneOf(selectedInspection.yard_id)}
          onClose={() => setSelectedInspection(null)}
          onSave={handleSaveYardInspection}
          onSaveRegistration={handleSaveInspectionRegistration}
          onUploadEvidence={handleUploadEvidence}
          onUpdateEvidence={handleUpdateEvidence}
          onDeleteEvidence={handleDeleteEvidence}
          onManualExit={handleManualExit}
        />
      )}

      {/* Report modal */}
      {reportInspection && (
        <InspectionReportModal
          inspection={reportInspection}
          vehicle={
            reportInspection.vehicle_id
              ? (vehiclesById.get(reportInspection.vehicle_id) ?? null)
              : null
          }
          onClose={() => setReportInspection(null)}
        />
      )}

      {/* Sync modal — same controller-log sync as the access screen */}
      {syncOpen && !noAssignedYards && (
        <SyncModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          yards={syncTargets.yards}
          // `locked` también es true con el alcance en `none`, y ahí el patio
          // efectivo es "" — falsy, o sea select DESBLOQUEADO. Ese caso no llega
          // acá porque el modal no se monta; el `!noAssignedYards` de arriba es
          // la mitad que lo garantiza, no un adorno.
          lockedYardId={yardFilter.locked ? firstSelectedYard : undefined}
          // Con un patio elegido arranca en ése; con varios, en "todos" sobre
          // una lista que ya son sólo los elegidos.
          defaultYardId={syncTargets.defaultYardId}
          onClose={() => setSyncOpen(false)}
          onSubmit={handleSync}
          submitting={syncing}
        />
      )}
    </div>
  );
}
